import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader, AlertCircle, CheckCircle, MapPin, X, Clock, Share2, Phone, Facebook, Instagram, Twitter, Linkedin, Youtube, MessageCircle, Send, Download, Volume2, Pause, Play } from 'lucide-react';
import { useTTS } from '../../hooks/useTTS.js';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import QRCode from 'react-qrcode-logo';
import kahaLogo from '../../assets/Group 627709.svg';
import { LocationResolver } from '../../domain/services/LocationResolver.js';
import QRPlate from '../qr-plate/QRPlate.jsx';

export const LoadingScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
            <div className="relative w-16 h-16">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-orange-500 rounded-full animate-spin"></div>
                <div className="absolute inset-2 bg-gray-900 rounded-full"></div>
                <div className="absolute inset-0 flex items-center justify-center"><Loader className="w-6 h-6 text-green-500" /></div>
            </div>
            <div className="text-center">
                <p className="text-white text-lg font-semibold mb-2">Loading business details</p>
                <div className="flex gap-1 justify-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                </div>
            </div>
        </div>
    </div>
);

export const ErrorScreen = ({ error, navigate }) => (
    <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="max-w-7xl mx-auto px-4 py-8 flex-1">
            <button onClick={() => navigate('/map')} className="flex items-center gap-2 text-green-600 hover:text-green-700 mb-6">
                <ArrowLeft className="w-5 h-5" /> Back to Map
            </button>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                    <h3 className="text-red-800 font-semibold mb-1">Error Loading Business</h3>
                    <p className="text-red-700">{error || 'Business not found'}</p>
                </div>
            </div>
        </div>
    </div>
);

export const HeroSection = ({ vm }) => {
    const { business, currentImage, qrPlateData, setShowQRPlateModal, handleShare, handleImageClick } = vm;
    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-3xl shadow-2xl mb-8 overflow-hidden backdrop-blur-xl border border-white/20 hover:border-green-400/50 transition-all duration-500 group">
            <div className="relative h-96 bg-gradient-to-br from-orange-400/20 to-orange-600/20 overflow-hidden cursor-pointer" onClick={() => currentImage && handleImageClick(currentImage)}>
                {currentImage ? (
                    <><img src={currentImage} alt={business.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /><div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div></>
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-500/20 to-orange-600/20"><MapPin className="w-20 h-20 text-white/20" /></div>
                )}
            </div>

            <div className="px-3 sm:px-4 py-4 sm:py-6 relative">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                    {business.avatar && (
                        <div className="flex-shrink-0 relative -mt-16 sm:-mt-20 cursor-pointer" onClick={() => handleImageClick(business.avatar)}>
                            <div className="absolute inset-0 bg-gradient-to-br from-green-400/30 to-orange-400/30 rounded-2xl blur-2xl"></div>
                            <img src={business.avatar} alt={business.name} className="relative w-24 sm:w-32 h-24 sm:h-32 rounded-2xl object-cover border-4 border-white/30 shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105" />
                        </div>
                    )}

                    <div className="flex-1 pt-0 sm:pt-2 w-full">
                        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
                            <div className="flex-1 w-full">
                                <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-white via-green-200 to-orange-300 bg-clip-text text-transparent mb-1 sm:mb-2 drop-shadow-lg">{business.name}</h1>
                                {business.tagLine && <p className="text-gray-300 text-xs sm:text-base italic mb-2 sm:mb-3 drop-shadow">{business.tagLine}</p>}
                                <div className="flex gap-2 flex-wrap mb-2 sm:mb-3">
                                    {business.category && (
                                        <span className="bg-gradient-to-r from-orange-500/30 to-orange-600/20 text-orange-200 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold border border-orange-400/50 backdrop-blur-sm shadow-lg flex items-center gap-2">
                                            {business.category.iconUrl && (
                                                <img src={business.category.iconUrl} alt={business.category.name} className="w-4 h-4 object-contain" />
                                            )}
                                            {business.category.name}
                                        </span>
                                    )}
                                    {business.tag && <span className="bg-gradient-to-r from-green-500/30 to-green-600/20 text-green-200 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold border border-green-400/50 backdrop-blur-sm shadow-lg">@{business.tag}</span>}
                                    {business.status?.value && (
                                        <span className="bg-gradient-to-r from-green-500/30 to-green-600/20 text-green-200 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold flex items-center gap-1 border border-green-400/50 backdrop-blur-sm shadow-lg"><CheckCircle className="w-3 h-3" />Verified</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                                {business.contact && (
                                    <a href={`tel:${business.contact}`} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-bold py-2 px-3 sm:px-4 rounded-lg flex items-center gap-2 shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all text-xs sm:text-sm flex-1 sm:flex-none justify-center">
                                        <Phone className="w-3 sm:w-4 h-3 sm:h-4" /> <span className="hidden sm:inline">Contact</span>
                                    </a>
                                )}
                                {qrPlateData && (
                                    <button onClick={() => setShowQRPlateModal(true)} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-bold py-2 px-3 sm:px-4 rounded-lg flex items-center gap-2 shadow-lg hover:shadow-blue-500/50 hover:scale-105 transition-all text-xs sm:text-sm flex-1 sm:flex-none justify-center">
                                        <span className="hidden sm:inline">View QR Plate</span><span className="sm:hidden">QR</span>
                                    </button>
                                )}
                                <button onClick={handleShare} className="bg-gradient-to-r from-white/20 to-white/10 hover:from-white/30 hover:to-white/20 text-white py-2 px-2 sm:px-3 rounded-lg backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg hover:scale-105 transition-all">
                                    <Share2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AboutSection = ({ vm }) => {
    if (!vm.business.description) return null;
    
    const enableTTS = import.meta.env.VITE_ENABLE_TTS === 'true';
    const { speak, pause, resume, stop, isSpeaking, isPaused, error } = useTTS();
    const aboutContent = vm.getTranslatedDescription();
    
    const languageNames = {
        'US': 'English',
        'NP': 'नेपाली',
        'IN': 'हिन्दी',
        'FR': 'Français',
        'ES': 'Español',
        'IT': 'Italiano',
        'JP': '日本語',
        'CN': '中文',
        'BD': 'বाংলা'
    };
    
    const languageOrder = ['US', 'NP', 'IN', 'FR', 'ES', 'IT', 'JP', 'CN', 'BD'];
    
    const sortedLanguages = vm.translations?.translationContent 
        ? [...vm.translations.translationContent]
            .filter(t => t.code !== 'NP-NE')
            .sort((a, b) => {
                const indexA = languageOrder.indexOf(a.code);
                const indexB = languageOrder.indexOf(b.code);
                return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
              })
        : [];
    
    const handleSpeak = () => {
        if (isSpeaking && !isPaused) {
            pause();
        } else if (isPaused) {
            resume();
        } else {
            speak(aboutContent, vm.selectedLanguage);
        }
    };
    
    return (
        <div ref={vm.aboutSectionRef} className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-green-400/50 transition-all duration-500 group">
            <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-3">
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">About {vm.business.name.split(' ')[0]}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {sortedLanguages.length > 0 && (
                        <select 
                            value={vm.selectedLanguage} 
                            onChange={(e) => {
                                vm.setSelectedLanguage(e.target.value);
                                if (enableTTS && isSpeaking) stop();
                            }}
                            className="px-3 py-1 bg-gray-800 text-white border border-green-400/50 rounded-lg text-xs sm:text-sm hover:border-green-400 transition-colors"
                        >
                            <option value="US">{languageNames['US']}</option>
                            {sortedLanguages.map(t => (
                                t.code !== 'US' && <option key={t.code} value={t.code}>{languageNames[t.code] || t.code}</option>
                            ))}
                        </select>
                    )}
                    {enableTTS && (
                        <>
                            <button
                                onClick={handleSpeak}
                                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold transition-all border ${
                                    isSpeaking
                                        ? 'bg-green-500/30 text-green-300 border-green-400/50 hover:bg-green-500/40'
                                        : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                                }`}
                                title={isSpeaking && !isPaused ? 'Pause' : isSpeaking && isPaused ? 'Resume' : 'Listen'}
                            >
                                {isSpeaking && !isPaused ? (
                                    <>
                                        <Pause className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Pause</span>
                                    </>
                                ) : isSpeaking && isPaused ? (
                                    <>
                                        <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Resume</span>
                                    </>
                                ) : (
                                    <>
                                        <Volume2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Listen</span>
                                    </>
                                )}
                            </button>
                            {isSpeaking && (
                                <button
                                    onClick={stop}
                                    className="px-2 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-red-500/20 text-red-300 border border-red-400/50 hover:bg-red-500/30 transition-all"
                                    title="Stop"
                                >
                                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            {enableTTS && error && (
                <div className="mb-3 p-2 bg-yellow-500/20 border border-yellow-400/50 rounded-lg text-xs text-yellow-300">
                    {error}
                </div>
            )}
            <div>
                <p className="text-gray-200 leading-relaxed whitespace-pre-wrap text-sm sm:text-base group-hover:text-white transition-colors break-words overflow-hidden">
                    {vm.expandedDescription ? aboutContent : vm.getTruncatedContent(aboutContent)}
                </p>
                {vm.isLongContent(aboutContent) && (
                    <button onClick={() => { vm.setExpandedDescription(!vm.expandedDescription); setTimeout(() => vm.aboutSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); }} className="mt-3 text-green-400 hover:text-green-300 font-bold text-xs sm:text-sm transition-all duration-300 flex items-center gap-2">
                        {vm.expandedDescription ? '▼ Show Less' : '▶ Show More'}
                    </button>
                )}
            </div>
        </div>
    );
};

export const GeneralInfoCard = ({ business }) => (
    <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-orange-400/50 transition-all duration-500">
        <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-orange-200 bg-clip-text text-transparent mb-4">General Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {business.category && <InfoBox title="CATEGORY" value={business.category.name} icon={business.category.iconUrl} />}
            {business.contact && <InfoBox title="PHONE" value={business.contact} isPhone />}
            {business.email && <InfoBox title="EMAIL" value={business.email} isEmail />}
        </div>
    </div>
);

export const InfoBox = ({ title, value, isTag, isPhone, isEmail, icon }) => (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10 hover:border-orange-400/50 transition-all hover:bg-white/10 min-w-0">
        <p className="text-gray-400 text-xs font-bold mb-1 uppercase tracking-wider flex items-center gap-2">
            {isPhone && <Phone className="w-4 h-4 text-green-400 flex-shrink-0" />}
            {isEmail && <MessageCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />}
            {icon && <img src={icon} alt={title} className="w-4 h-4 object-contain flex-shrink-0" />}
            <span className="truncate">{title}</span>
        </p>
        {isTag ? <p className="text-orange-300 hover:text-orange-200 font-semibold text-sm sm:text-base transition-colors truncate">{value}</p>
            : isPhone ? <a href={`tel:${value}`} className="text-green-300 hover:text-green-200 font-semibold text-sm sm:text-base transition-colors truncate block">{value}</a>
                : isEmail ? <a href={`mailto:${value}`} className="text-blue-300 hover:text-blue-200 font-semibold text-xs sm:text-sm truncate block transition-colors">{value}</a>
                    : <p className="text-white font-semibold text-sm sm:text-base truncate">{value}</p>
        }
    </div>
);

export const ServiceBadgesCard = ({ business }) => {
    if (business.category?.name === 'Home') return null;
    const hasAnyService = business.available !== undefined || business.delivery !== undefined || business.pickup !== undefined;
    if (!hasAnyService) return null;

    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-emerald-400/50 transition-all duration-500">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-emerald-200 bg-clip-text text-transparent mb-4">Services</h2>
            <div className="flex flex-wrap gap-3">
                {business.available !== undefined && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-xs sm:text-sm border backdrop-blur-sm ${business.available ? 'bg-gradient-to-r from-green-500/30 to-green-600/20 text-green-200 border-green-400/50' : 'bg-white/5 text-gray-400 border-white/10'}`}>
                        <div className={`w-2 h-2 rounded-full ${business.available ? 'bg-green-400' : 'bg-gray-500'}`}></div>
                        Available Now
                    </div>
                )}
                {business.delivery !== undefined && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-xs sm:text-sm border backdrop-blur-sm ${business.delivery ? 'bg-gradient-to-r from-blue-500/30 to-blue-600/20 text-blue-200 border-blue-400/50' : 'bg-white/5 text-gray-400 border-white/10'}`}>
                        <div className={`w-2 h-2 rounded-full ${business.delivery ? 'bg-blue-400' : 'bg-gray-500'}`}></div>
                        Delivery
                    </div>
                )}
                {business.pickup !== undefined && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-xs sm:text-sm border backdrop-blur-sm ${business.pickup ? 'bg-gradient-to-r from-orange-500/30 to-orange-600/20 text-orange-200 border-orange-400/50' : 'bg-white/5 text-gray-400 border-white/10'}`}>
                        <div className={`w-2 h-2 rounded-full ${business.pickup ? 'bg-orange-400' : 'bg-gray-500'}`}></div>
                        Pickup
                    </div>
                )}
            </div>
        </div>
    );
};

export const PhotoGalleryCard = ({ vm }) => {
    if (!vm.images || vm.images.length === 0) return null;
    
    // Booking.com style layout: 1 large + 2 medium on right + row of thumbnails
    const mainImage = vm.images[0];
    const sideImages = vm.images.slice(1, 3);
    const thumbnails = vm.images.slice(3, 8);
    const remainingCount = vm.images.length - 8;

    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-6 backdrop-blur-xl border border-white/20 hover:border-purple-400/50 transition-all duration-500">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">Photo Gallery</h2>
                {vm.images.length > 1 && (
                    <button 
                        onClick={() => { vm.setActiveImageIndex(0); vm.setShowGalleryModal(true); }} 
                        className="text-purple-300 hover:text-purple-200 font-semibold text-sm transition-colors flex items-center gap-2"
                    >
                        <span>View All ({vm.images.length})</span>
                    </button>
                )}
            </div>

            {/* Mobile: Show only main image + thumbnails */}
            <div className="md:hidden">
                {/* Main image */}
                <button 
                    onClick={() => { vm.setActiveImageIndex(0); vm.setShowGalleryModal(true); }}
                    className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/20 hover:border-purple-400/50 transition-all group relative mb-3"
                >
                    <img 
                        src={mainImage} 
                        alt="Main gallery image" 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </button>

                {/* Thumbnail row on mobile - show all remaining images */}
                {vm.images.length > 1 && (
                    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(vm.images.length - 1, 5)}, 1fr)` }}>
                        {vm.images.slice(1).map((img, idx) => (
                            <button 
                                key={idx}
                                onClick={() => { vm.setActiveImageIndex(idx + 1); vm.setShowGalleryModal(true); }}
                                className="aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/20 hover:border-purple-400/50 transition-all group relative"
                            >
                                <img 
                                    src={img} 
                                    alt={`Thumbnail ${idx + 1}`} 
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                                />
                                {/* Show +X photos on last thumbnail if there are more */}
                                {idx === 4 && vm.images.length > 6 && (
                                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm">
                                        <span className="text-white font-bold text-sm">+{vm.images.length - 6}</span>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Desktop: Booking.com style grid layout */}
            <div className="hidden md:grid grid-cols-3 gap-2 mb-2">
                {/* Large main image - takes 2 columns and 2 rows */}
                <button 
                    onClick={() => { vm.setActiveImageIndex(0); vm.setShowGalleryModal(true); }}
                    className="col-span-2 row-span-2 aspect-auto rounded-xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/20 hover:border-purple-400/50 transition-all group relative"
                >
                    <img 
                        src={mainImage} 
                        alt="Main gallery image" 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </button>

                {/* Two stacked images on the right - each takes 1 row */}
                {sideImages.map((img, idx) => (
                    <button 
                        key={idx}
                        onClick={() => { vm.setActiveImageIndex(idx + 1); vm.setShowGalleryModal(true); }}
                        className="aspect-auto rounded-xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/20 hover:border-purple-400/50 transition-all group relative"
                    >
                        <img 
                            src={img} 
                            alt={`Gallery ${idx + 1}`} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                    </button>
                ))}
            </div>

            {/* Thumbnail row below - desktop only */}
            {thumbnails.length > 0 && (
                <div className="hidden md:grid grid-cols-5 gap-2">
                    {thumbnails.map((img, idx) => (
                        <button 
                            key={idx}
                            onClick={() => { vm.setActiveImageIndex(idx + 3); vm.setShowGalleryModal(true); }}
                            className="aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/20 hover:border-purple-400/50 transition-all group relative"
                        >
                            <img 
                                src={img} 
                                alt={`Thumbnail ${idx + 3}`} 
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                            />
                            {/* Show +X photos on last thumbnail if there are more */}
                            {idx === thumbnails.length - 1 && remainingCount > 0 && (
                                <div className="absolute inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm">
                                    <span className="text-white font-bold text-sm">+{remainingCount}</span>
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const LocationAccessCard = ({ business }) => {
    const [locationNames, setLocationNames] = useState({});
    const [viewMode, setViewMode] = useState('map'); // 'map', 'streetview', or 'virtualtour'
    const [showFullscreenView, setShowFullscreenView] = useState(false);
    const [streetViewAvailable, setStreetViewAvailable] = useState(null); // null = checking, true = available, false = not available

    useEffect(() => {
        const resolveLocations = async () => {
            await LocationResolver.initialize();

            if (business?.mapAddress) {
                const resolved = LocationResolver.resolveAddress({
                    province: business.mapAddress.province,
                    district: business.mapAddress.district,
                    municipality: business.mapAddress.municipality,
                }, 'english'); // Using 'english' format for cleaner display

                setLocationNames(resolved || {});
            }
        };

        resolveLocations();
    }, [business?.mapAddress]);

    // Check Street View availability
    useEffect(() => {
        if (!business?.location?.coordinates) return;

        const checkStreetViewAvailability = async () => {
            try {
                const [lat, lng] = business.location.coordinates;
                const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
                
                if (!apiKey) {
                    console.warn('Google Maps API key not found');
                    setStreetViewAvailable(false);
                    return;
                }

                const response = await fetch(
                    `https://maps.googleapis.com/maps/api/streetview/metadata?location=${lat},${lng}&key=${apiKey}`
                );
                const data = await response.json();
                console.log('Street View availability check:', data);
                setStreetViewAvailable(data.status === 'OK');
            } catch (error) {
                console.error('Error checking Street View availability:', error);
                // Assume available on error - let the iframe handle it
                setStreetViewAvailable(true);
            }
        };

        checkStreetViewAvailability();
    }, [business?.location?.coordinates]);

    const coords = business?.location?.coordinates;

    useEffect(() => {
        console.log('Business data:', business);
        console.log('KahaTag:', business?.kahaTag);
    }, [business]);

    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-cyan-400/50 transition-all duration-500 overflow-hidden">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent mb-4">Location & Access</h2>
            
            {/* Address Info */}
            {business.address && (
                <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white/10 hover:border-cyan-400/50 transition-all flex gap-2">
                    <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <p className="text-gray-300 text-xs sm:text-sm mb-0.5 break-words">{business.address}</p>
                        {business.mapAddress?.generalAddress && <p className="text-gray-400 text-xs">{business.mapAddress.generalAddress}</p>}
                    </div>
                </div>
            )}

            {/* Map/Street View/Virtual Tour Toggle */}
            {coords && (
                <div className="mb-4 flex gap-2">
                    <button
                        onClick={() => setViewMode('map')}
                        className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                            viewMode === 'map'
                                ? 'bg-cyan-500/30 border border-cyan-400 text-cyan-300'
                                : 'bg-white/10 border border-white/20 text-gray-300 hover:bg-white/20'
                        }`}
                    >
                        Map
                    </button>
                    <button
                        onClick={() => setViewMode('streetview')}
                        disabled={streetViewAvailable === false}
                        className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                            viewMode === 'streetview'
                                ? 'bg-cyan-500/30 border border-cyan-400 text-cyan-300'
                                : streetViewAvailable === false
                                ? 'bg-gray-700/30 border border-gray-600/50 text-gray-500 cursor-not-allowed'
                                : 'bg-white/10 border border-white/20 text-gray-300 hover:bg-white/20'
                        }`}
                        title={streetViewAvailable === false ? 'Street View not available for this location' : 'View Street View'}
                    >
                        Street View {streetViewAvailable === false && '(N/A)'}
                    </button>
                    {business.tag === 'HOTELMUSTANG' || business.name?.includes('HOTELMUSTANG') ? (
                        <button
                            onClick={() => setViewMode('virtualtour')}
                            className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                                viewMode === 'virtualtour'
                                    ? 'bg-cyan-500/30 border border-cyan-400 text-cyan-300'
                                    : 'bg-white/10 border border-white/20 text-gray-300 hover:bg-white/20'
                            }`}
                            title="View Virtual Tour"
                        >
                            Virtual Tour
                        </button>
                    ) : null}
                </div>
            )}

            {/* Inline Map */}
            {coords && viewMode === 'map' && (
                <div className="mb-4 rounded-3xl overflow-hidden border border-white/20 h-80 shadow-lg relative">
                    <button
                        onClick={() => setShowFullscreenView(true)}
                        className="absolute top-4 right-4 bg-cyan-500 hover:bg-cyan-600 text-white p-2.5 rounded-full transition-all z-10 shadow-lg hover:scale-110"
                        title="View in fullscreen"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                        </svg>
                    </button>
                    <MapContainer
                        center={[coords[0], coords[1]]}
                        zoom={18}
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={false}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                            maxZoom={19}
                        />
                        <Marker position={[coords[0], coords[1]]}>
                            <Popup>
                                <div className="p-2 bg-white rounded">
                                    <h3 className="font-bold text-black text-sm">{business.name}</h3>
                                    <p className="text-xs text-gray-600 mt-1">{business.address}</p>
                                    {business.mapAddress?.wardNo && (
                                        <p className="text-xs text-gray-500 mt-1">Ward {business.mapAddress.wardNo}</p>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    </MapContainer>
                </div>
            )}

            {/* Google Street View */}
            {coords && viewMode === 'streetview' && (
                <div className="mb-4 rounded-3xl overflow-hidden border border-white/20 h-80 shadow-lg bg-gray-900 relative flex items-center justify-center w-full">
                    {streetViewAvailable ? (
                        <>
                            <button
                                onClick={() => setShowFullscreenView(true)}
                                className="absolute top-4 right-4 bg-cyan-500 hover:bg-cyan-600 text-white p-2.5 rounded-full transition-all z-10 shadow-lg hover:scale-110"
                                title="View in fullscreen"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                                </svg>
                            </button>
                            <iframe
                                key={`streetview-${coords[0]}-${coords[1]}`}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                src={`https://www.google.com/maps/embed/v1/streetview?location=${coords[0]},${coords[1]}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Street View"
                            />
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-4 p-6">
                            <svg className="w-20 h-20 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4" />
                            </svg>
                            <div className="text-center">
                                <p className="text-gray-300 font-semibold mb-1">N/A</p>
                                <p className="text-gray-400 text-sm">Street View not available for this location</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Virtual Tour */}
            {viewMode === 'virtualtour' && business.tag === 'HOTELMUSTANG' && (
                <div className="mb-4 rounded-3xl overflow-hidden border border-white/20 h-80 shadow-lg bg-gray-900 relative flex items-center justify-center w-full">
                    <button
                        onClick={() => setShowFullscreenView(true)}
                        className="absolute top-4 right-4 bg-cyan-500 hover:bg-cyan-600 text-white p-2.5 rounded-full transition-all z-10 shadow-lg hover:scale-110"
                        title="View in fullscreen"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                        </svg>
                    </button>
                    <iframe
                        width="100%"
                        height="100%"
                        style={{ border: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        src="https://hotelmustangholidayinn.com/virtualtour/"
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            )}

            {/* Fullscreen Modal */}
            {showFullscreenView && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm rounded-3xl">
                    {/* Close Button */}
                    <button
                        onClick={() => setShowFullscreenView(false)}
                        className="absolute top-6 right-6 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-all z-20 shadow-lg hover:scale-110"
                        title="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Modal Container */}
                    <div className="w-full max-w-6xl h-[90vh] rounded-3xl overflow-hidden border-2 border-white/20 shadow-2xl bg-gray-900">
                        {/* Fullscreen Map */}
                        {viewMode === 'map' && (
                            <MapContainer
                                center={[coords[0], coords[1]]}
                                zoom={18}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                                    maxZoom={19}
                                />
                                <Marker position={[coords[0], coords[1]]}>
                                    <Popup>
                                        <div className="p-2 bg-white rounded">
                                            <h3 className="font-bold text-black text-sm">{business.name}</h3>
                                            <p className="text-xs text-gray-600 mt-1">{business.address}</p>
                                            {business.mapAddress?.wardNo && (
                                                <p className="text-xs text-gray-500 mt-1">Ward {business.mapAddress.wardNo}</p>
                                            )}
                                        </div>
                                    </Popup>
                                </Marker>
                            </MapContainer>
                        )}

                        {/* Fullscreen Street View */}
                        {viewMode === 'streetview' && (
                            streetViewAvailable ? (
                                <iframe
                                    key={`streetview-fullscreen-${coords[0]}-${coords[1]}`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 'none', display: 'block' }}
                                    src={`https://www.google.com/maps/embed/v1/streetview?location=${coords[0]},${coords[1]}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Street View"
                                />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 gap-6 p-8">
                                    <svg className="w-24 h-24 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4" />
                                    </svg>
                                    <div className="text-center">
                                        <h3 className="text-white text-xl font-bold mb-2">Street View Not Available</h3>
                                        <p className="text-gray-400 mb-6">No street view imagery is available for this location. Please use the map view to see the location.</p>
                                        <button
                                            onClick={() => setViewMode('map')}
                                            className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-semibold transition-all"
                                        >
                                            View on Map
                                        </button>
                                    </div>
                                </div>
                            )
                        )}

                        {/* Fullscreen Virtual Tour */}
                        {viewMode === 'virtualtour' && business.tag === 'HOTELMUSTANG' && (
                            <iframe
                                width="100%"
                                height="100%"
                                style={{ border: 'none', display: 'block' }}
                                src="https://hotelmustangholidayinn.com/virtualtour/"
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Get Directions Button */}
            {business.address && coords && (
                <button 
                    onClick={() => {
                        const [lat, lng] = coords;
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                        window.open(mapsUrl, '_blank');
                    }}
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white font-bold py-2 px-3 rounded-lg transition-all shadow-lg hover:scale-105 text-sm mb-4"
                    title="Open in Google Maps"
                >
                    Get Directions
                </button>
            )}

            {/* Building & Floor */}
            {business.buildingInformation && (
                <div className="mb-3 pb-3 border-b border-white/10">
                    <p className="text-gray-400 text-xs font-bold mb-1 uppercase tracking-wider">BUILDING</p>
                    <p className="text-white font-semibold text-sm">{business.buildingInformation}</p>
                </div>
            )}
            {business.floorNo && (
                <div className="mb-4">
                    <p className="text-gray-400 text-xs font-bold mb-1 uppercase tracking-wider">FLOOR</p>
                    <p className="text-white font-semibold text-sm">{business.floorNo}</p>
                </div>
            )}

            {/* Location Details with Resolved Names */}
            {(business.mapAddress?.wardNo || locationNames.municipality || locationNames.district || locationNames.province) && (
                <div className="pb-0 border-t border-white/10 pt-4">
                    <p className="text-gray-400 text-xs font-bold mb-4 uppercase tracking-wider">Location Details</p>
                    <div className="space-y-2.5">
                        {business.mapAddress?.wardNo && (
                            <div className="flex">
                                <span className="w-24 sm:w-28 text-gray-400 font-medium text-sm">Ward</span>
                                <span className="text-gray-300 text-sm">: {business.mapAddress.wardNo}</span>
                            </div>
                        )}
                        {locationNames.municipality && (
                            <div className="flex">
                                <span className="w-24 sm:w-28 text-gray-400 font-medium text-sm">Municipality</span>
                                <span className="text-gray-300 text-sm">: {locationNames.municipality}</span>
                            </div>
                        )}
                        {locationNames.district && (
                            <div className="flex">
                                <span className="w-24 sm:w-28 text-gray-400 font-medium text-sm">District</span>
                                <span className="text-gray-300 text-sm">: {locationNames.district}</span>
                            </div>
                        )}
                        {locationNames.province && (
                            <div className="flex">
                                <span className="w-24 sm:w-28 text-gray-400 font-medium text-sm">Province</span>
                                <span className="text-gray-300 text-sm">: {locationNames.province}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const EmergencyContactsCard = ({ category }) => {
    if (category !== 'Home') return null;
    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-red-400/50 transition-all duration-500">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-red-200 bg-clip-text text-transparent mb-4">Emergency Contacts</h2>
            <div className="grid grid-cols-2 gap-3">
                <EmergencyLink tel="100" np="१००" enText="Police" npText="प्रहरी" color="red" />
                <EmergencyLink tel="101" np="१०१" enText="Fire Brigade" npText="दमकल" color="orange" />
                <EmergencyLink tel="102" np="१०२" enText="Ambulance" npText="एम्बुलेन्स" color="green" />
                <EmergencyLink tel="104" np="१०४" enText="Missing Children" npText="बालबालिका खोजतलास" color="blue" />
            </div>
        </div>
    );
};

export const EmergencyLink = ({ tel, np, enText, npText, color }) => (
    <a href={`tel:${tel}`} className={`bg-gradient-to-br from-${color}-500/20 to-${color}-600/10 border border-${color}-400/50 hover:border-${color}-400 rounded-lg p-3 transition-all hover:scale-105 group text-center`}>
        <p className={`text-${color}-300 font-bold text-lg group-hover:text-${color}-200 transition-colors`}>{np}</p>
        <p className={`text-gray-300 text-xs sm:text-sm group-hover:text-white transition-colors`}>{enText}</p>
        <p className="text-gray-400 text-xs mt-1">{npText}</p>
    </a>
);

export const OpeningHoursCard = ({ business, workingHours, isCurrentlyOpen, convertTo12Hour }) => {
    if (!workingHours || Object.keys(workingHours).length === 0 || business.category?.name === 'Home') return null;
    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    // Get today's day name (lowercase)
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    
    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-yellow-400/50 transition-all duration-500">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-yellow-400" /> Opening Hours</h2>
            <div className="space-y-1.5 mb-3">
                {daysOrder.map(day => {
                    if (!workingHours[day]) return null;
                    const isToday = day === today;
                    return (
                        <div key={day} className={`flex justify-between items-center py-1.5 px-2 rounded-lg transition-all ${isToday ? 'bg-yellow-500/20 border border-yellow-400/50' : 'bg-white/5 border border-white/10 hover:border-yellow-400/50'}`}>
                            <span className={`font-semibold capitalize text-xs sm:text-sm ${isToday ? 'text-yellow-300' : 'text-gray-200'}`}>{day}</span>
                            <span className={`text-xs sm:text-sm ${isToday ? 'text-yellow-300' : 'text-gray-400'}`}>{convertTo12Hour(workingHours[day])}</span>
                        </div>
                    );
                })}
            </div>
            {isCurrentlyOpen && isCurrentlyOpen(workingHours) && (
                <div className="bg-gradient-to-r from-green-500/30 to-green-600/20 border border-green-400/50 rounded-lg p-2 flex items-center gap-2 backdrop-blur-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" /><span className="text-green-200 font-bold text-xs sm:text-sm">CURRENTLY OPEN</span>
                </div>
            )}
        </div>
    );
};

export const SocialLinksCard = ({ socialLinks }) => {
    if (!socialLinks || socialLinks.length === 0) return null;
    const getSocialIcon = (iconName) => {
        const iconProps = { className: "w-4 sm:w-5 h-4 sm:h-5 text-current" };
        switch (iconName?.toLowerCase()) {
            case 'facebook': return <Facebook {...iconProps} />;
            case 'instagram': return <Instagram {...iconProps} />;
            case 'twitter':
            case 'x': return <Twitter {...iconProps} />;
            case 'linkedin': return <Linkedin {...iconProps} />;
            case 'youtube': return <Youtube {...iconProps} />;
            case 'whatsapp': return <MessageCircle {...iconProps} />;
            case 'telegram': return <Send {...iconProps} />;
            default: return null;
        }
    };
    return (
        <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-4 sm:p-6 backdrop-blur-xl border border-white/20 hover:border-pink-400/50 transition-all duration-500">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-pink-200 bg-clip-text text-transparent mb-4">Follow Us</h2>
            <div className="flex flex-wrap gap-2">
                {socialLinks.map(link => (
                    <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-gradient-to-r from-white/10 to-white/5 hover:from-pink-500/30 text-gray-200 hover:text-pink-200 font-semibold py-1.5 px-3 rounded-lg transition-all border border-white/20 hover:scale-105 text-xs sm:text-sm" title={link.name}>
                        {getSocialIcon(link.icon)} <span className="hidden sm:inline">{link.name}</span>
                    </a>
                ))}
            </div>
        </div>
    );
};

export const RelatedBusinesses = ({ businesses, navigate }) => {
    const [showAll, setShowAll] = useState(false);
    
    if (!businesses || businesses.length === 0) return null;
    
    // Mobile: show 2 initially, Desktop: show all
    const displayedBusinesses = showAll ? businesses : businesses.slice(0, 2);
    
    return (
        <div className="mt-12">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-white via-green-200 to-orange-300 bg-clip-text text-transparent mb-8 drop-shadow-lg">Similar Businesses</h2>
            
            {/* Mobile view: show 2 with See More button */}
            <div className="md:hidden">
                <div className="grid grid-cols-1 gap-6">
                    {displayedBusinesses.map(b => (
                        <div key={b.id} className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/20 hover:border-green-400/50 transition-all hover:scale-105 group">
                            {b.avatar && (
                                <div className="relative h-48 overflow-hidden">
                                    <img src={b.avatar} alt={b.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                </div>
                            )}
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors truncate" title={b.name}>{b.name}</h3>
                                <p className="text-gray-300 text-sm mb-3 truncate" title={b.address}>{b.address}</p>
                                {b.category && (
                                    <p className="text-xs text-gray-400 mb-2 flex items-center gap-2">
                                        {typeof b.category === 'string' ? (
                                            <>📁 {b.category}</>
                                        ) : (
                                            <>
                                                {b.category.iconUrl && <img src={b.category.iconUrl} alt={b.category.name} className="w-4 h-4 object-contain" />}
                                                {b.category.name}
                                            </>
                                        )}
                                    </p>
                                )}
                                {b.contact && <p className="text-xs text-gray-400 mb-2 truncate">📞 {b.contact}</p>}
                                <button onClick={() => navigate(`/entity/${b.tag}`)} className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 text-white font-bold py-2 px-4 rounded-lg transition-all shadow-lg hover:shadow-green-500/50">View Profile</button>
                            </div>
                        </div>
                    ))}
                </div>
                {businesses.length > 2 && (
                    <div className="mt-6 flex justify-center">
                        <button
                            onClick={() => setShowAll(!showAll)}
                            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold py-3 px-8 rounded-lg transition-all shadow-lg hover:shadow-green-500/50"
                        >
                            {showAll ? 'Show Less' : `See More (${businesses.length - 2})`}
                        </button>
                    </div>
                )}
            </div>

            {/* Desktop view: show all without button */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
                {businesses.map(b => (
                    <div key={b.id} className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/20 hover:border-green-400/50 transition-all hover:scale-105 group">
                        {b.avatar && (
                            <div className="relative h-48 overflow-hidden">
                                <img src={b.avatar} alt={b.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                            </div>
                        )}
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors truncate" title={b.name}>{b.name}</h3>
                            <p className="text-gray-300 text-sm mb-3 truncate" title={b.address}>{b.address}</p>
                            {b.category && (
                                <p className="text-xs text-gray-400 mb-2 flex items-center gap-2">
                                    {typeof b.category === 'string' ? (
                                        <>📁 {b.category}</>
                                    ) : (
                                        <>
                                            {b.category.iconUrl && <img src={b.category.iconUrl} alt={b.category.name} className="w-4 h-4 object-contain" />}
                                            {b.category.name}
                                        </>
                                    )}
                                </p>
                            )}
                            {b.contact && <p className="text-xs text-gray-400 mb-2 truncate">📞 {b.contact}</p>}
                            <button onClick={() => navigate(`/entity/${b.tag}`)} className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 text-white font-bold py-2 px-4 rounded-lg transition-all shadow-lg hover:shadow-green-500/50">View Profile</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const GalleryModal = ({ vm }) => {
    if (!vm.showGalleryModal || !vm.images || vm.images.length === 0) return null;
    return (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center backdrop-blur-sm p-4">
            {/* Close Button */}
            <button 
                onClick={() => vm.setShowGalleryModal(false)} 
                className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all z-20"
            >
                <X className="w-6 h-6" />
            </button>

            {/* Main Image */}
            <div className="relative w-full max-w-5xl flex items-center justify-center mb-4" style={{ height: '75vh' }}>
                <img 
                    src={vm.images[vm.activeImageIndex]} 
                    alt={`Gallery ${vm.activeImageIndex}`} 
                    className="w-full h-full object-contain rounded-lg"
                />

                {/* Image Counter */}
                <div className="absolute top-4 left-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {vm.activeImageIndex + 1} / {vm.images.length}
                </div>

                {/* Left Arrow */}
                <button 
                    onClick={() => vm.setActiveImageIndex(p => p === 0 ? vm.images.length - 1 : p - 1)} 
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all z-10"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                {/* Right Arrow */}
                <button 
                    onClick={() => vm.setActiveImageIndex(p => p === vm.images.length - 1 ? 0 : p + 1)} 
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all z-10"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Thumbnails - Smaller Size */}
            <div className="w-full max-w-5xl">
                <div className="flex gap-2 justify-center flex-wrap">
                    {vm.images.map((img, idx) => (
                        <button 
                            key={idx} 
                            onClick={() => vm.setActiveImageIndex(idx)} 
                            className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all hover:scale-110 ${
                                idx === vm.activeImageIndex 
                                    ? 'border-orange-500 ring-2 ring-orange-500 scale-110' 
                                    : 'border-gray-600 hover:border-gray-400'
                            }`}
                        >
                            <img src={img} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const MapModal = ({ vm }) => {
    const coords = vm.business?.location?.coordinates || (vm.business?.mapAddress?.latitude ? [parseFloat(vm.business.mapAddress.latitude), parseFloat(vm.business.mapAddress.longitude)] : null);
    if (!vm.showMapModal || !coords) return null;
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-white/95 to-white/90 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto backdrop-blur-sm border border-white/20 shadow-2xl">
                <div className="sticky top-0 bg-gradient-to-r from-white/95 to-white/90 border-b border-gray-200 p-4 flex justify-between items-center backdrop-blur-sm z-10">
                    <h2 className="text-2xl font-bold text-gray-900">{vm.business.name} Location</h2>
                    <button onClick={() => vm.setShowMapModal(false)} className="text-gray-500 hover:text-gray-700 transition-colors"><X className="w-6 h-6" /></button>
                </div>
                <div className="p-6">
                    <div className="rounded-lg overflow-hidden h-96 border border-gray-200 shadow-lg">
                        <MapContainer center={[coords[0], coords[1]]} zoom={16} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; KAHA' />
                            <Marker position={[coords[0], coords[1]]}>
                                <Popup><div className="p-2"><h3 className="font-bold text-black">{vm.business.name}</h3><p className="text-sm text-gray-600">{vm.business.address || vm.business.mapAddress?.generalAddress}</p></div></Popup>
                            </Marker>
                        </MapContainer>
                    </div>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-600 text-sm mb-2 font-semibold">Address:</p><p className="text-gray-900 font-bold text-lg">{vm.business.address || vm.business.mapAddress?.generalAddress}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const QRPlateModal = ({ vm }) => {
    if (!vm.showQRPlateModal || !vm.qrPlateData) return null;
    return (
        <QRPlate
            qrPlateData={vm.qrPlateData}
            businessId={vm.business?.id}
            businessTag={vm.business?.tag}
            onClose={() => vm.setShowQRPlateModal(false)}
            showDownloadButtons={!!vm.handleDownloadQR || !!vm.isOwner}
        />
    );
};


export const ImagePreviewModal = ({ showImagePreview, setShowImagePreview, previewImage }) => {
    if (!showImagePreview || !previewImage) return null;

    return (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="relative w-full h-full flex items-center justify-center">
                {/* Close Button */}
                <button
                    onClick={() => setShowImagePreview(false)}
                    className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Image - Fit entire screen */}
                <img
                    src={previewImage}
                    alt="Preview"
                    className="w-full h-full object-contain"
                />

                {/* Close on click outside */}
                <div
                    className="absolute inset-0 -z-10"
                    onClick={() => setShowImagePreview(false)}
                />
            </div>
        </div>
    );
};
